import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, Info, Upload } from "lucide-react";
import { POSTFORMDATA, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import * as XLSX from "xlsx";
import EntityConstants from "../../configs/EntityConstants";
import UploadedPdfGif from "../../resources/icons/upload_pdf_zip.gif";
import ZipStructureModal from "../../common/ZipStructureModal/ZipStructureModal";
import {
  createDragHandlers,
  createDropHandler,
} from "../../common/DragDropUtils/dragDropUtils";
import {
  validateExcelFile,
  validateZipFile,
} from "../../common/FileValidationUtils/fileValidationUtils";
import useNavigationStore from "../../store/navigationStore";

export default function LegacyCertificateDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { issuer_credential_template_id: paramLegacyTransactionId } =
    useParams();
  const { transactionData, fromResume } = location.state || {};
  const queryParams = new URLSearchParams(location.search);
  const initialStep = fromResume ? 2 : queryParams.get("step") === "2" ? 2 : 1;

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    file: null,
    recipientsFile: null,
    commonColumn: "",
  });
  const [fileUploaded, setFileUploaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [issuerCredentialTemplateId, setIssuerCredentialTemplateId] = useState(
    paramLegacyTransactionId || null
  );
  const [legacyTransactionId, setLegacyTransactionId] = useState(null);

  const [isGifHovered, setIsGifHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [recipientsDragActive, setRecipientsDragActive] = useState(false);

  const zipDragHandlers = createDragHandlers(setDragActive);
  const recipientsDragHandlers = createDragHandlers(setRecipientsDragActive);
  const [programmeName, setProgrammeName] = useState("");
  const [batchName, setBatchName] = useState("");
  const maxCharsIssuanceName = 45;
  const maxCharsIssuanceDescription = 255;

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, navigate]);

  const handleZipDrop = createDropHandler(setDragActive, (file) => {
    const validation = validateZipFile(file);
    if (!validation.isValid) {
      Toast({
        type: "error",
        title: "Error",
        message: validation.error,
      });
      return;
    }
    setFormData((prev) => ({ ...prev, file }));
  });

  const handleRecipientsDrop = createDropHandler(
    setRecipientsDragActive,
    async (file) => {
      const validation = validateExcelFile(file);
      if (!validation.isValid) {
        Toast({
          type: "error",
          title: "Error",
          message: validation.error,
        });
        return;
      }

      setFormData((prev) => ({ ...prev, recipientsFile: file }));
      setFileUploaded(true);

      try {
        const headers = await extractHeaders(file);
        setCsvHeaders(headers);
      } catch (error) {
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to read file headers",
        });
      }
    }
  );

  useEffect(() => {
    let initialData = {
      name: transactionData?.transaction_name || "",
      description: transactionData?.description || "",
      file: transactionData?.file_name
        ? { name: transactionData.file_name }
        : null,
      recipientsFile: null,
      commonColumn: "",
      is_nad: transactionData?.is_nad || false,
    };

    if (initialStep === 2) {
      initialData = {
        ...initialData,
        name:
          transactionData?.transaction_name ||
          initialData.name ||
          "Transaction",
        description:
          transactionData?.description || initialData.description || "",
        file: transactionData?.file_name
          ? { name: transactionData.file_name }
          : formData.file || { name: "certificates.zip" },
      };
      setLegacyTransactionId(transactionData?.legacy_transaction_id);
    }

    setFormData(initialData);
  }, [initialStep, transactionData, paramLegacyTransactionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (
      (name === "name" && value.length <= maxCharsIssuanceName) ||
      (name === "description" && value.length <= maxCharsIssuanceDescription)
    ) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validation = validateZipFile(file);
      if (!validation.isValid) {
        Toast({
          type: "error",
          title: "Error",
          message: validation.error,
        });
        return;
      }
      setFormData((prev) => ({
        ...prev,
        file: file,
      }));
    }
  };

  const handleRecipientsFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validation = validateExcelFile(file);
      if (!validation.isValid) {
        Toast({
          type: "error",
          title: "Error",
          message: validation.error,
        });
        return;
      }

      setFormData((prev) => ({ ...prev, recipientsFile: file }));
      setFileUploaded(true);

      // Extract headers from CSV/Excel file
      try {
        const headers = await extractHeaders(file);
        setCsvHeaders(headers);
      } catch (error) {
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to read file headers",
        });
      }
    }
  };

  const extractHeaders = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const isNAD = transactionData?.is_nad ?? formData?.is_nad ?? false;

          let sheetName;
          if (isNAD) {
            //! For NAD, just use the first sheet
            sheetName = workbook.SheetNames[0];
          } else {
            //! For non-NAD, find the 'data' sheet
            sheetName = workbook.SheetNames.find(
              (name) => name.toLowerCase() === "data"
            );
          }

          if (!sheetName) {
            throw new Error(
              isNAD
                ? "No sheets found in the Excel file"
                : "No 'data' sheet found in the Excel file"
            );
          }

          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            range: 0, // Only read the first row
          });
          const headers = jsonData[0] || []; // First row contains headers

          resolve(headers);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  };

  const handleCommonColumnChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      commonColumn: e.target.value,
    }));
  };

  const fetchSampleUrls = async (transId) => {
    try {
      const response = await GET(
        `${EntityConstants.DOWNLOAD_SAMPLE_ZIP_EXCEL}/${transId}/sample`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching sample URLs:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch sample files",
      });
      return null;
    }
  };

  const downloadXLXS = async () => {
    try {
      setIsDownloading(true);
      const currentTransId =
        issuerCredentialTemplateId || paramLegacyTransactionId;
      const sampleUrls = await fetchSampleUrls(currentTransId);
      if (!sampleUrls || !sampleUrls.excel_sample_url) {
        throw new Error("Excel sample URL not available");
      }
      const link = document.createElement("a");
      link.href = sampleUrls.excel_sample_url;
      link.download = `Sample_Recipients_Data.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      Toast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to download sample Excel",
      });
    } finally {
      // Use setTimeout to ensure the download has started before resetting the flag
      setTimeout(() => setIsDownloading(false), 1000);
    }
  };

  const downloadSampleZip = async () => {
    try {
      setIsDownloading(true);
      const currentTransId =
        issuerCredentialTemplateId || paramLegacyTransactionId;
      const sampleUrls = await fetchSampleUrls(currentTransId);
      const link = document.createElement("a");
      link.href = sampleUrls.zip_sample_url;
      link.download = `Sample_Certificates.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      Toast({
        type: "error",
        title: "Error",
        message: error.message || "Failed to download sample ZIP",
      });
    } finally {
      setTimeout(() => setIsDownloading(false), 1000);
    }
  };

  const handleNextStep = async () => {
    if (!formData.name || !formData.file) {
      Toast({
        type: "error",
        title: "Error",
        message: "Please fill all required fields and upload the ZIP file.",
      });
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("zip_file", formData.file);
      formDataToSend.append("issuer_template_id", paramLegacyTransactionId);
      formDataToSend.append("transaction_name", formData.name);
      formDataToSend.append("description", formData.description);

      const response = await POSTFORMDATA(
        `${EntityConstants.POST_ZIP_PDF_UPLOAD}`,
        formDataToSend
      );

      const data = await response.json();

      if (response.ok) {
        setFormData((prev) => ({
          ...prev,
          name: data.data.transaction_name,
          description: data.data.description,
          file: prev.file,
          legacy_transaction_id: data.data.legacy_transaction_id,
          credential_template_id: data.data.credential_template_id,
          is_nad: data.data.is_nad,
        }));
        setIssuerCredentialTemplateId(data.data.issuer_credential_template_id);
        setLegacyTransactionId(data.data.legacy_transaction_id);
        setCurrentStep(2);
      } else {
        throw new Error(data.error || "Failed to upload ZIP file");
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const showExcelValidationIssues = (data) => {
    if (!data) return;

    const mainError =
      data.error || "Validation failed. Some required fields are missing.";

    if (Array.isArray(data.details)) {
      const missingCols =
        data.details.find((d) => d.type === "missing_columns")
          ?.missing_columns || [];
      const missingFields =
        data.details.find((d) => d.type === "missing_values")?.missing_fields ||
        [];

      if (missingCols.length) {
        Toast({
          type: "error",
          title: "Error",
          message: `${mainError}\nMissing Columns: ${missingCols.join(", ")}`,
        });
      }

      if (missingFields.length) {
        Toast({
          type: "error",
          title: "Error",
          message: `${mainError}\nMissing Values: ${missingFields.join(", ")}`,
        });
      }

      if (!missingCols.length && !missingFields.length) {
        Toast({
          type: "error",
          title: "Error",
          message: mainError,
        });
      }
    } else {
      Toast({
        type: "error",
        title: "Error",
        message: mainError,
      });
    }
  };

  const handleViewSummary = async () => {
    if (!formData.recipientsFile || !formData.commonColumn) {
      Toast({
        type: "error",
        title: "Error",
        message:
          "Please upload the recipients file and select the common column.",
      });
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("excel_file", formData.recipientsFile);
      formDataToSend.append("common_column", formData.commonColumn);

      const isNadValue = transactionData?.is_nad ?? formData?.is_nad;
      if (isNadValue !== undefined) {
        formDataToSend.append("is_nad", isNadValue.toString());
      }

      const response = await POSTFORMDATA(
        `${EntityConstants.POST_VIEW_SUMMARY}?${new URLSearchParams({
          legacy_transction_log_id:
            (legacyTransactionId || transactionData?.legacy_transaction_id) +
            "",
        }).toString()}`,
        formDataToSend
      );

      const data = await response.json();

      if (response.ok) {
        navigate(
          `/issuer/workspace/batches/legacy-summary/${data.legacy_transcation_data.legacy_transaction_id}/${data.legacy_transcation_data.issuer_credential_template_id}`
        );
      } else {
        showExcelValidationIssues(data);
        return;
      }
    } catch (error) {
      Toast({
        type: "error",
        title: "Error",
        message: error?.message || "Excel upload failed",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentStep === 2 && !isDownloading) {
        e.preventDefault();
        e.returnValue = "Unsaved changes! Do you really want to refresh?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [currentStep, isDownloading]);

  // Handle browser refresh
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Detect F5 or Ctrl+R
      if (
        (e.key === "F5" || (e.ctrlKey && e.key === "r")) &&
        currentStep === 2
      ) {
        e.preventDefault();
        const confirmRefresh = window.confirm(
          "Unsaved changes! Do you really want to refresh?"
        );
        if (confirmRefresh) {
          navigate(`/issuer/workspace/batches/credentials`);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentStep, navigate]);

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace`);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleTemplatesClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const fetchProgrammeName = () => {
    GET(EntityConstants.GET_PROGRAMME_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.programme_display_name) {
          setProgrammeName(data.programme_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching programme name:", error);
      });
  };
  const fetchBatchName = () => {
    GET(EntityConstants.GET_BATCH_NAME)
      .then((response) => response.json())
      .then((data) => {
        if (data.batch_display_name) {
          setBatchName(data.batch_display_name);
        }
      })
      .catch((error) => {
        console.error("Error fetching batch name:", error);
      });
  };
  useEffect(() => {
    fetchProgrammeName();
    fetchBatchName();
  }, []);

  if (!currentProgramme || !currentBatch) {
    return (
      <div className="w-full mt-20 bg-cfSecondary min-h-screen flex justify-center items-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-cfSecondary mt-20">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full`}
        >
          <div className="">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                <div className="flex items-center gap-2 text-sm text-cfTextBreadcrumbDisabled">
                  <button
                    type="button"
                    onClick={handleProgrammeClick}
                    className="hover:text-cfTextBreadcrumbActive cursor-pointer focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleProgrammeClick();
                      }
                    }}
                  >
                    {currentProgramme.programme_name || "Programme"}
                  </button>
                  <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                  <button
                    type="button"
                    onClick={handleBatchClick}
                    className="hover:text-cfTextBreadcrumbActive cursor-pointer focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleBatchClick();
                      }
                    }}
                  >
                    {currentBatch.batch_name || "Batch"}
                  </button>
                  <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                  <button
                    type="button"
                    onClick={handleTemplatesClick}
                    className="hover:text-cfTextBreadcrumbActive cursor-pointer focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleTemplatesClick();
                      }
                    }}
                  >
                    Certificates
                  </button>
                  <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                  <span className="text-cfTextBreadcrumbActive font-medium">
                    Summary
                  </span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>
      <div className="pt-3 pl-4 pr-4">
        <div className="mb-4">
          <header className="text-xl font-semibold text-gray-900 mb-2">
            Issue Legacy Certificate
          </header>
          <div className="flex justify-center">
            <div className="flex items-center">
              <div className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep === 1
                      ? "bg-cfPrimaryButton text-cfSecondary"
                      : "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfSecondary"
                  }`}
                >
                  1
                </div>
                <span className="ml-2 font-medium">Basic Information</span>
              </div>
              <div
                className={`w-16 h-1 mx-2 ${
                  currentStep === 1
                    ? "bg-gray-200"
                    : "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                }`}
              ></div>
              <div className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep === 2
                      ? "bg-cfPrimaryButton text-cfSecondary"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  2
                </div>
                <span className="ml-2 font-medium">Configure Issuance</span>
              </div>
            </div>
          </div>
        </div>
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <CfLoader />
          </div>
        )}
        {currentStep === 1 && (
          <div className="flex flex-col lg:flex-row w-full gap-2">
            <div className="bg-cfSecondary border rounded-lg p-6 w-full lg:w-[65%]">
              <div className="flex flex-col">
                <div className="space-y-2">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-cfSignInSignUpInputLabelText mb-1"
                  >
                    Issuance Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter a name for this issuance"
                    required
                    className="w-full px-3 py-2 border border-cfSignInSignUpInputBorder rounded-lg shadow-sm focus:outline-none"
                  />
                  <div className="text-right text-sm mt-2">
                    <span>
                      {maxCharsIssuanceName - formData.name.length} characters
                      left
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-cfSignInSignUpInputLabelText mb-1"
                  >
                    Description (Optional)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter a description for this issuance"
                    rows={4}
                    className="w-full px-3 py-2 border border-cfSignInSignUpInputBorder rounded-lg shadow-sm focus:outline-none"
                  />
                  <div className="text-right text-sm mt-2">
                    <span>
                      {maxCharsIssuanceDescription -
                        formData.description.length}{" "}
                      characters left
                    </span>
                  </div>
                </div>

                <div className="mt-6 space-y-2">
                  <div className="flex justify-between">
                    <label
                      htmlFor="file"
                      className="block text-sm font-medium text-cfSignInSignUpInputLabelText mb-1"
                    >
                      Upload ZIP File
                    </label>
                    {(paramLegacyTransactionId || currentStep === 2) && (
                      <button
                        type="button"
                        className="text-sm text-cfPrimaryLight hover:text-cfDontHaveAccountSignUpText focus:outline-none cursor-pointer"
                        onClick={downloadSampleZip}
                      >
                        Download Sample ZIP
                      </button>
                    )}
                  </div>
                  <div
                    className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
                      dragActive
                        ? "bg-blue-50 border-blue-300"
                        : "hover:bg-gray-50"
                    }`}
                    {...zipDragHandlers}
                    onDrop={handleZipDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        document.getElementById("file").click();
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".zip"
                      id="file"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    <label
                      htmlFor="file"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-gray-400" />
                      <span className="text-sm font-medium">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-500">
                        ZIP file containing certificate PDFs
                      </span>
                    </label>
                    {formData.file && (
                      <div className="mt-2 text-sm text-cfDontHaveAccountSignUpText font-medium">
                        Selected: {formData.file.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Link to={`/issuer/workspace/batches/credentials`}>
                    <button className="px-4 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      Cancel
                    </button>
                  </Link>
                  <button
                    className={`px-4 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cfDontHaveAccountSignUpText hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      !formData.name || !formData.file
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleNextStep}
                    disabled={!formData.name || !formData.file || loading}
                  >
                    {loading ? "Processing..." : "Next"}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-cfSecondary border rounded-lg p-6 w-full lg:w-[35%]">
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 border-none rounded-lg">
                  <div className="flex items-center">
                    <p className="text-[#3538CD] text-md">
                      Issuing certificates for{" "}
                      <span className="font-semibold">
                        {currentProgramme.programme_name || "Programme"}
                      </span>{" "}
                      {programmeName}{" "}
                      <span className="font-semibold">
                        {currentBatch.batch_name || "Batch"}
                      </span>{" "}
                      {batchName}{" "}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-cfDontHaveAccountSignUpText" />
                  <h3 className="text-lg font-medium">
                    How to Prepare Your ZIP File
                  </h3>
                </div>
                {isModalOpen && (
                  <ZipStructureModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    UploadedPdfGif={UploadedPdfGif}
                    header={"ZIP File Structure Demonstration"}
                    description={
                      "How to properly structure your certificate files"
                    }
                  />
                )}
                <div
                  className="border rounded-lg overflow-hidden relative cursor-pointer"
                  onMouseEnter={() => setIsGifHovered(true)}
                  onMouseLeave={() => setIsGifHovered(false)}
                  onClick={() => setIsModalOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setIsModalOpen(true);
                    }
                  }}
                >
                  <img
                    src={UploadedPdfGif}
                    alt="ZIP file structure demonstration"
                    className="w-full"
                  />
                  {isGifHovered && (
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <div className="bg-white bg-opacity-80 rounded-full p-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-gray-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3 text-sm text-gray-600">
                  <p>
                    Your ZIP file should contain all certificate PDFs that you
                    want to issue.
                  </p>

                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-gray-800 mb-2">
                      File Naming Requirements:
                    </h4>
                    <ul className="space-y-1 list-disc pl-5">
                      <li>
                        Each PDF file should be named according to the
                        recipient&apos;s identifier
                      </li>
                      <li>
                        For example, if using email as the common column, name
                        your files as{" "}
                        <span className="font-mono bg-gray-100 px-1 rounded">
                          email@example.com.pdf
                        </span>
                      </li>
                      <li>
                        File names should match exactly with the data in your
                        recipients file
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-3 rounded-md">
                    <h4 className="font-medium text-gray-800 mb-2">
                      Example Structure:
                    </h4>
                    <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">
                      certificates.zip ├── john@example.com.pdf ├──
                      jane@example.com.pdf ├── robert@example.com.pdf └──
                      michael@example.com.pdf
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {currentStep === 2 && (
        <div className="pl-4 pr-4 pb-4">
          <div className="bg-cfSecondary border rounded-lg p-6 w-full">
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="recipients-file"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Upload Recipients Data
                    </label>
                    {(issuerCredentialTemplateId ||
                      paramLegacyTransactionId) && (
                      <button
                        type="button"
                        className="text-sm text-cfPrimaryLight hover:text-cfDontHaveAccountSignUpText focus:outline-none cursor-pointer"
                        onClick={downloadXLXS}
                      >
                        Download Sample Excel
                      </button>
                    )}
                  </div>
                  <div
                    className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
                      recipientsDragActive
                        ? "bg-blue-50 border-blue-300"
                        : "hover:bg-gray-50"
                    }`}
                    {...recipientsDragHandlers}
                    onDrop={handleRecipientsDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        document.getElementById("recipients-file").click();
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      id="recipients-file"
                      className="hidden"
                      onChange={handleRecipientsFileChange}
                    />
                    <label
                      htmlFor="recipients-file"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="h-8 w-8 text-gray-400" />
                      <span className="text-sm font-medium">
                        Click to upload or drag and drop
                      </span>
                      <span className="text-xs text-gray-500">
                        Excel file with recipient information
                      </span>
                    </label>
                    {formData.recipientsFile && (
                      <div className="mt-2 text-sm text-cfPrimaryLight font-medium">
                        Selected: {formData.recipientsFile.name}
                      </div>
                    )}
                  </div>
                </div>
                {fileUploaded && (
                  <div className="space-y-2 pt-4 border-t">
                    <label
                      htmlFor="common-column"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Common Column
                    </label>
                    <select
                      id="common-column"
                      value={formData.commonColumn}
                      onChange={handleCommonColumnChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select a column from your data</option>
                      {csvHeaders.map((header) => (
                        <option key={header} value={header}>
                          {header}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select the column that will be used to match recipients
                      with certificate files
                    </p>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    File Requirements:
                  </h3>
                  <ul className="text-xs text-gray-500 space-y-1 list-disc pl-5">
                    <li>Excel (.xlsx, .xls) format</li>
                    <li>First row must contain column headers</li>
                    <li>Required columns: Name, Email</li>
                    <li>Optional columns: ID, Phone, Custom fields</li>
                  </ul>
                </div>
              </div>

              {/* Right side - Summary */}
              <div className="col-span-1 bg-gray-50 p-4 rounded-md">
                <div className="p-3 bg-blue-50 border-none rounded-lg mb-3">
                  <div className="flex items-center">
                    <p className="text-[#3538CD] text-md">
                      Issuing certificates for{" "}
                      <span className="font-semibold">
                        {currentProgramme.programme_name || "Programme"}
                      </span>{" "}
                      {programmeName}{" "}
                      <span className="font-semibold">
                        {currentBatch.batch_name || "Batch"}
                      </span>{" "}
                      {batchName}{" "}
                    </p>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Issuance Summary
                </h3>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs text-gray-500">Issuance Name</h4>
                    <p className="text-sm font-medium">
                      {transactionData?.transaction_name || formData.name}
                    </p>
                  </div>

                  {formData.description && (
                    <div>
                      <h4 className="text-xs text-gray-500">Description</h4>
                      <p className="text-sm">
                        {transactionData?.description || formData.description}
                      </p>
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs text-gray-500">Certificate Files</h4>
                    <div className="flex items-center mt-1 bg-white p-2 rounded border">
                      <div className="bg-indigo-100 p-1 rounded mr-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-indigo-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-xs truncate">
                        {transactionData?.zip_file_name ||
                          formData.file?.name ||
                          "No file selected"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-6 mt-6 border-t">
              <button
                onClick={() => setCurrentStep(1)}
                className={`px-4 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 ${
                  fromResume ? "opacity-50 cursor-not-allowed" : "bg-white"
                } hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                disabled={loading || fromResume}
              >
                Back
              </button>
              <button
                className={`px-4 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cfDontHaveAccountSignUpText hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  !formData.recipientsFile ||
                  (fileUploaded && !formData.commonColumn)
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={handleViewSummary}
                disabled={
                  !formData.recipientsFile ||
                  (fileUploaded && !formData.commonColumn) ||
                  loading
                }
              >
                {loading ? "Processing..." : "View Summary"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
